// Fee-waiver additional-info-tag helpers (SOR-150, FWR-2/FWR-3/FWR-8).
//
// The Document Assignments step renders one checkbox per court-supplied
// `additionalInfoTag`. The fee-waiver tag's checkbox used to be write-only dead
// state: it wrote a `${meta.code}_FLAGS` bag that nothing reads, while the
// party's REAL fee-waiver state lives in the party-level fields
// (`feeExemptionType` / `requestFeesExemption` / `feeWaiverOnFile`) that drive
// the fee calc and envelope. These helpers let the checkbox be DERIVED from
// that single source of truth (FWR-3) and labelled consistently (FWR-8),
// entirely within user-portal — no shared-contracts / pipeline / golden churn.

/**
 * One consistent label for the fee-waiver concept across the party detail
 * screen and Document Assignments (FWR-8). Matches the "Fee Waiver on File"
 * checkbox on the party detail form (`AddPartyModal`), replacing the cosmetic
 * `EFSP_`-stripping derivation ("Fee Waiver Filed") that named the old
 * additional-info checkbox.
 */
export const FEE_WAIVER_TAG_LABEL = 'Fee Waiver on File';

/**
 * True when a court-supplied `additionalInfoTag` names the party fee-waiver
 * concept. The only known runtime string is `EFSP_FEE_WAIVER_FILED` (JTI
 * document-metadata model doc), but the tag is court-supplied, so anchor on
 * `FEE_WAIVER` after an optional `EFSP_` prefix. Deliberately does NOT match
 * `FEE_EXEMPTION` — a distinct structural tag the envelope genuinely emits
 * (stage-3-envelope.ts) — so this branch never hijacks an unrelated control.
 */
export function isFeeWaiverInfoTag(tag: string): boolean {
  return /^(?:EFSP_)?FEE_WAIVER(?:_|$)/.test(tag);
}

/** Structural subset of a party the derivation reads — mirrors the shared
 *  `FeeExemptionPartyLike` (order-logic/common/fee-waiver.ts) so the wizard
 *  display and the fee calc can never disagree on what "has a waiver" means. */
export interface FeeWaiverPartyLike {
  feeExemptionType?: string | null;
  requestFeesExemption?: boolean;
  feeWaiverOnFile?: boolean;
}

/**
 * Party-level fee-waiver derivation — the single source of truth (FWR-3).
 * Mirrors the shared `effectiveFeeExemption` semantics without importing across
 * the `@lds-efsp/shared-contracts` barrel (that predicate is not re-exported,
 * and FWR-3 is about the data, not the reader): an explicit `FEE_WAIVER`
 * exemption, the legacy `requestFeesExemption` boolean, or an existing
 * `feeWaiverOnFile` each mean this party has a fee waiver.
 */
export function partyHasFeeWaiver(party: FeeWaiverPartyLike): boolean {
  return (
    party.feeExemptionType === 'FEE_WAIVER' ||
    party.requestFeesExemption === true ||
    party.feeWaiverOnFile === true
  );
}
